import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { MoreVertical, Pencil, Trash2, MapPin, Home, Shield } from 'lucide-react';
import { PROJECT_TYPES, PROJECT_STAGES, getStageColor } from './projectConstants';

export const ProjectCard = ({ project, index, placeholderImages, user, isReadOnly, onEdit, onDelete }) => {
  return (
    <Card 
      className="overflow-hidden hover-lift hover-glow group"
      data-testid={`project-card-${project.project_id}`}
    >
      <div className="aspect-video bg-muted relative overflow-hidden">
        <img
          src={project.image_url || placeholderImages[index % placeholderImages.length]}
          alt={project.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          <Badge className={getStageColor(project.project_stage)}>
            {PROJECT_STAGES.find(s => s.value === project.project_stage)?.label || project.project_stage}
          </Badge>
        </div>
        <div className="absolute top-3 right-3">
          {(user?.role === 'admin' || user?.role === 'management') && !isReadOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="secondary" 
                  size="icon" 
                  className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`project-menu-${project.project_id}`}
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(project)}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onDelete(project.project_id)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-semibold text-lg">{project.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline">{project.code}</Badge>
              <Badge variant="secondary" className="capitalize">
                {PROJECT_TYPES.find(t => t.value === project.project_type)?.label || project.project_type}
              </Badge>
            </div>
          </div>
        </div>
        {project.location && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
            <MapPin className="w-4 h-4" />
            {project.location}
          </div>
        )}
        <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
          <Home className="w-4 h-4" />
          {project.total_units} units
        </div>
        {project.rera_registered && project.rera_number && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
            <Shield className="w-4 h-4 text-emerald-600" />
            RERA: {project.rera_number}
          </div>
        )}
        {project.description && (
          <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
            {project.description}
          </p>
        )}
        <div className="mt-4">
          <Button variant="outline" className="w-full" asChild>
            <a href={`/inventory?project=${project.project_id}`}>
              View Inventory
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
